//dependencies
import React from "react";

import { faMagnifyingGlass, faPen } from "@fortawesome/pro-regular-svg-icons";

import { GetEmployeeDetailResponse } from "@performyard/service-contracts";

import Alert from "../../../../../components/Alert";
import Button from "../../../../../components/Button";
import Card from "../../../../../components/Card";
//components
import Container from "../../../../../components/Container";
import Input from "../../../../../components/Inputs/InputText";
import Link from "../../../../../components/Link";
import Spacer from "../../../../../components/Spacer";
import Typography from "../../../../../components/Text";
import Tooltip from "../../../../../components/Tooltip";

//hooks
import { useEmailTokens, useEmployeeCustomFields, useRbacEmployeePermissions } from "../../../../../api/hooks";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";

import { getCustomFieldDisplayValue, getIntegrationImage } from "../../../utils";

//config
import Config from "../../../Config";
//styles
import css from "../../EmployeeDetails.module.scss";

type ViewEmployeeCardProps = {
	employeeId: string;
};

const ViewEmployeeCard = ({ employeeId }: ViewEmployeeCardProps) => {
	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: Config.viewPageSchema });
	const search = queryParams.search || "";
	const setSearch = (value: string) => handleQueryChange({ search: value });
	const { data = { customFields: [] } } = useEmployeeCustomFields(employeeId, {
		enabled: !!employeeId,
	});

	const { data: rbac } = useRbacEmployeePermissions(employeeId, "employee_details");

	const canEmployeeViewHistory =
		rbac?.includes("employee_details:viewAdminFieldHistory") ||
		rbac?.includes("employee_details:viewSelfFieldHistory") ||
		rbac?.includes("employee_details:viewManagerFieldHistory");

	const { data: emailTokens } = useEmailTokens(employeeId);
	const isPendingEmailChange = (emailTokens?.tokens?.length || 0) > 0;

	const customFields = search
		? data.customFields.filter(
				(field) =>
					field.tags?.some((tag) => tag.toLowerCase().includes(search.toLowerCase())) ||
					field.name.toLowerCase().includes(search.toLowerCase())
			)
		: data.customFields;

	const userCanEdit = data.customFields.some((field) => field.editable);
	const isUserConnectedToIntegration = data.customFields.some(
		(field) => field.user?.externalIds && Object.keys(field.user?.externalIds)?.[0]
	);

	//grab static fields
	const firstNameField = customFields.find((field) => field.name === Config.STATIC_FIELDS[0]);
	const lastNameField = customFields.find((field) => field.name === Config.STATIC_FIELDS[1]);
	const emailField = customFields.find((field) => field.name === Config.STATIC_FIELDS[2]);
	const nickNameField = customFields.find((field) => field.name === Config.STATIC_FIELDS[3]);

	const getCustomFieldRow = (field: GetEmployeeDetailResponse, alertMessage: string = "") => {
		// do not display activation status
		if (field.name === "Activation Status") {
			return null;
		}
		if (
			!search ||
			field.tags?.some((tag) => tag.toLowerCase().includes(search.toLowerCase())) ||
			field.name.toLowerCase().includes(search.toLowerCase())
		) {
			let integrationImage: string | null = null;

			if (field?.isScimManaged) {
				integrationImage = getIntegrationImage({ scim: true });
			} else if (isUserConnectedToIntegration) {
				integrationImage = getIntegrationImage(field?.integrations || {});
			}

			return (
				<Container
					column
					gap={"8px"}
					marginBottom={"16px"}
					className={css.customFieldRow}
					key={field._id.toString()}
					testId={field.name.toLowerCase().replace(/\s/g, "-")}
				>
					{canEmployeeViewHistory ? (
						<Container gap="4px" verticalAlignment="center">
							<Tooltip
								className={css.tooltip}
								key={field._id.toString()}
								message="View Field History"
								placement="top"
							>
								<Link href={`/view-field-history/${field._id.toString()}/${employeeId}`}>
									<Typography
										className={css.selectableFieldHistoryLabel}
										size="paragraph-3"
										weight="semi-bold"
										color="neutral-9"
									>
										{field.name}
									</Typography>
								</Link>
							</Tooltip>

							{integrationImage && (
								<img
									className={css.integrationImage}
									src={integrationImage}
									alt=""
									role="presentation"
								/>
							)}
						</Container>
					) : (
						<Container gap="4px" verticalAlignment="center">
							<Typography
								className={css.fieldHistoryLabel}
								size="paragraph-3"
								weight="semi-bold"
								color="neutral-9"
							>
								{field.name}
							</Typography>
							{integrationImage && (
								<img
									className={css.integrationImage}
									src={integrationImage}
									alt=""
									role="presentation"
								/>
							)}
						</Container>
					)}
					<div className={css.customFieldValue}>{getCustomFieldDisplayValue(field.type, field.value)}</div>
					{alertMessage ? <Alert type="error" message={alertMessage} /> : null}
				</Container>
			);
		} else {
			return null;
		}
	};

	return (
		<Card>
			<Container wrap horizontalAlignment="space-between" verticalAlignment="center">
				<Input
					className={css.search}
					placeholder="Search fields..."
					value={search}
					onChange={(value) => setSearch(value)}
					clearable
					icon={faMagnifyingGlass}
					iconPlacement="left"
				/>
				{userCanEdit && (
					<Button
						iconPosition="left"
						icon={faPen}
						isHollow
						color="gray"
						onClick={() => handleQueryChange({ editing: true, search: undefined })}
					>
						Edit
					</Button>
				)}
			</Container>
			<Spacer height={4} />
			{firstNameField && getCustomFieldRow(firstNameField)}
			{lastNameField && getCustomFieldRow(lastNameField)}
			{emailField &&
				getCustomFieldRow(
					emailField,
					isPendingEmailChange ? "Email address change is pending verification" : ""
				)}
			{nickNameField && getCustomFieldRow(nickNameField)}
			{customFields
				.filter((field) => !Config.STATIC_FIELDS.includes(field.name))
				.map((field) => getCustomFieldRow(field))}
		</Card>
	);
};

export default ViewEmployeeCard;
